import { Redirect, router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

import {
  CheckoutGiftCardActionSheet,
  GiftCardProvider,
  logDismissActionSheet,
  useGiftCardsActionSheet,
} from '@fhs/payments';
import { ActionSheet, tokens } from '@fhs/ui';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import type { IGiftCard } from '@fhs-legacy/frontend/src/state/payment/types';
import { CART_PAYMENT_PAGE } from '@fhs-legacy/frontend/src/utils/test-ids';

import { PaymentMethodSelection } from '../components/checkout-components/payment-details/action-payment-method-selection';
import { CartActionPickupTimeSelector } from '../components/checkout-components/service-mode-details/pickup/action-pickup-time-selector';
import { useCheckoutAndroidKeyboardMode } from '../hooks/use-checkout-android-keyboard-mode';
import { usePaymentMethodStore } from '../state/payment-stores';

import { CheckoutScreen as CheckoutScreen_ } from './_checkout';

const onClose = () => router.setParams({ action: undefined });

// UI timing guard for Android OEM variance (Samsung/One UI applies adjustPan later than Pixel). Raise to 200 if still jumps.
const ANDROID_KEYBOARD_MODE_DELAY_MS = 150;

export function CheckoutScreen() {
  useCheckoutAndroidKeyboardMode();

  const [keyboardModeReady, setKeyboardModeReady] = useState(Platform.OS !== 'android');
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const t = setTimeout(() => setKeyboardModeReady(true), ANDROID_KEYBOARD_MODE_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const cart = { state: undefined, rbiOrderId: undefined };
  const isPending = false;

  const { action } = useLocalSearchParams<{
    action: 'time' | 'service-mode' | 'payment';
  }>();
  const showTimeSelector = action === 'time';
  const showPaymentSelector = action === 'payment';
  const showServiceModeSelector = action === 'service-mode';

  const { logRBIEvent } = useCRMEventsContext();

  // Gift card action sheet state management
  const {
    actionSheetOpen,
    handleActionSheetOpen,
    handleClose: handleGiftCardActionSheetClose,
    handleRequestClose,
    currentStep,
    setCurrentStep,
    headerTitle,
    closeConfirmationOpen,
    handleConfirmStay,
    handleConfirmLeave,
    cardId,
    setCardId,
    pin,
    setPin,
  } = useGiftCardsActionSheet();

  const setSelectedPaymentMethod = usePaymentMethodStore(state => state.setSelectedPaymentMethod);
  const setShowAddPaymentMethod = usePaymentMethodStore(state => state.setShowAddPaymentMethod);
  const setPreservePaymentMethodSelection = usePaymentMethodStore(
    state => state.setPreservePaymentMethodSelection
  );
  const userAccountsData = usePaymentMethodStore(state => state.userAccountsData);
  const hasPreloadedUserAccounts = Boolean(userAccountsData?.userAccounts);
  const preloadedGiftCard = userAccountsData?.userAccounts?.accounts?.find(
    account => account?.giftCard
  )?.giftCard as IGiftCard | undefined;

  const onGiftCardConfirmLeave = useCallback(() => {
    logDismissActionSheet(logRBIEvent, 'Add Gift Card', currentStep);
    handleConfirmLeave();
  }, [logRBIEvent, handleConfirmLeave, currentStep]);

  const handleGiftCardSaved = useCallback(() => {
    setShowAddPaymentMethod(false);
    // Set payment method first (this resets preservePaymentMethodSelection to false)
    setSelectedPaymentMethod('giftCard');
    // Then set preserve flag AFTER to prevent auto-selection from overriding when userAccounts refetches
    setPreservePaymentMethodSelection(true);
    handleGiftCardActionSheetClose();
  }, [
    setSelectedPaymentMethod,
    setShowAddPaymentMethod,
    setPreservePaymentMethodSelection,
    handleGiftCardActionSheetClose,
  ]);

  if (isPending) {
    return (
      <View style={styles.page}>
        <ActivityIndicator />
      </View>
    );
  }

  if (!cart) {
    throw new Error('UndetectableCartError');
  }

  if (cart.state === 'INSERT_REQUESTED') {
    return null;
  }
  if (cart.state === 'INSERT_SUCCESSFUL' && cart.rbiOrderId) {
    return <Redirect href={`/order-confirmation/${cart.rbiOrderId}`} />;
  }

  return (
    <GiftCardProvider
      skipFetch={hasPreloadedUserAccounts}
      preloadedGiftCard={preloadedGiftCard ?? null}
    >
      <View testID={CART_PAYMENT_PAGE} style={styles.page}>
        <View style={styles.inner}>
          {keyboardModeReady ? <CheckoutScreen_ /> : null}
        </View>
      </View>
      {showTimeSelector && (
        <ActionSheet isVisible onClose={onClose}>
          <CartActionPickupTimeSelector onClose={onClose} />
        </ActionSheet>
      )}
      {showServiceModeSelector && (
        <ActionSheet isVisible onClose={onClose}>
          service mode
        </ActionSheet>
      )}
      {showPaymentSelector && (
        <ActionSheet isVisible onClose={onClose} isScrollableDrawer>
          <PaymentMethodSelection
            onClose={onClose}
            onAddNewGiftCardPress={handleActionSheetOpen}
          />
        </ActionSheet>
      )}

      <CheckoutGiftCardActionSheet
        onGiftCardSaved={handleGiftCardSaved}
        isVisible={actionSheetOpen}
        onClose={handleRequestClose}
        headerTitle={headerTitle}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        cardId={cardId}
        onCardIdChange={setCardId}
        pin={pin}
        onPinChange={setPin}
        closeConfirmationOpen={closeConfirmationOpen}
        onConfirmStay={handleConfirmStay}
        onConfirmLeave={onGiftCardConfirmLeave}
      />
    </GiftCardProvider>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: 'white',
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: 1,
    flex: 1,
  },
  inner: {
    flex: 1,
    width: '100%',
    maxWidth: 908,
    marginHorizontal: 'auto',
  },
});
